import React, { useState, useEffect } from 'react';
import { Flame } from 'lucide-react';

interface StreakDisplayProps {
  currentStreak: number;
  previousStreak: number;
  weekHistory: boolean[];
  colorScheme?: 'purple' | 'orange' | 'pink';
}

const WeekCalendar: React.FC<{ history: boolean[]; colorScheme?: 'purple' | 'orange' | 'pink' }> = ({ history, colorScheme = 'purple' }) => {
  const colorClasses = {
    purple: {
      completed: 'bg-purple-500',
      today: 'bg-blue-500'
    },
    orange: {
      completed: 'bg-orange-500',
      today: 'bg-blue-500'
    },
    pink: {
      completed: 'bg-pink-500',
      today: 'bg-blue-500'
    }
  };

  const colors = colorClasses[colorScheme];
  // Portuguese day labels: D (Domingo), S (Segunda), T (Terça), Q (Quarta), Q (Quinta), S (Sexta), S (Sábado)
  const days = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
  const todayIndex = new Date().getDay(); // 0 = Sun, 6 = Sat
  
  // Reorder days so the last one is todayIndex
  const rotatedDays = [];
  for (let i = 6; i >= 0; i--) {
    const dIndex = (todayIndex - i + 7) % 7;
    rotatedDays.push(days[dIndex]);
  }

  const displayHistory = history || new Array(7).fill(false);
  const isToday = (index: number) => index === displayHistory.length - 1;

  return (
    <div className="flex justify-between items-center mt-4 px-1">
      {displayHistory.map((isActive, index) => {
        const today = isToday(index);
        const isCompleted = isActive;
        
        return (
          <div key={index} className="flex flex-col items-center gap-1">
            <span className={`text-[10px] font-bold ${today ? 'text-gray-700' : 'text-gray-400'}`}>
              {rotatedDays[index]}
            </span>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
              isCompleted
                ? today
                  ? `${colors.today} text-white shadow-md ring-2 ring-offset-1 ring-blue-300`
                  : `${colors.completed} text-white shadow-sm`
                  : today
                  ? 'bg-gray-200 border-2 border-gray-400'
                  : 'bg-gray-100'
              }`}
            >
              {isCompleted ? (
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : today ? (
                <div className="w-2 h-2 bg-gray-400 rounded-full" />
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const StreakDisplay: React.FC<StreakDisplayProps> = ({ 
  currentStreak, 
  previousStreak, 
  weekHistory,
  colorScheme = 'purple'
}) => {
  const colorClasses = {
    purple: {
      flame: 'text-purple-500 fill-purple-500',
      text: 'text-purple-600',
      completed: 'bg-purple-500',
      today: 'bg-blue-500'
    },
    orange: {
      flame: 'text-orange-500 fill-orange-500',
      text: 'text-orange-600',
      completed: 'bg-orange-500',
      today: 'bg-blue-500'
    },
    pink: {
      flame: 'text-pink-500 fill-pink-500',
      text: 'text-pink-600',
      completed: 'bg-pink-500',
      today: 'bg-blue-500'
    }
  };

  const colors = colorClasses[colorScheme];
  const [displayStreak, setDisplayStreak] = useState(previousStreak);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (currentStreak !== previousStreak) {
      setIsAnimating(true);
      
      // Animate from previous to current streak
      const duration = 1000; // 1 second
      const steps = 30;
      const stepDuration = duration / steps;
      const increment = (currentStreak - previousStreak) / steps;
      
      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const newValue = Math.round(previousStreak + (increment * currentStep));
        
        if (currentStep >= steps) {
          setDisplayStreak(currentStreak);
          setIsAnimating(false);
          clearInterval(interval);
        } else {
          setDisplayStreak(Math.min(newValue, currentStreak));
        }
      }, stepDuration);

      return () => clearInterval(interval);
    } else {
      setDisplayStreak(currentStreak);
    }
  }, [currentStreak, previousStreak]);

  // Calculate days until next milestone (5, 10, 15, etc.)
  const getNextMilestone = (streak: number): number => {
    if (streak < 5) return 5;
    if (streak < 10) return 10;
    if (streak < 15) return 15;
    if (streak < 30) return 30;
    return Math.ceil((streak + 1) / 10) * 10;
  };

  const nextMilestone = getNextMilestone(currentStreak);
  const daysUntilMilestone = nextMilestone - currentStreak;

  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
      {/* Streak Icon and Count */}
      <div className="flex flex-col items-center mb-4">
        <div className={`relative mb-2 ${isAnimating ? 'animate-bounce' : ''}`}>
          <Flame className={`w-12 h-12 ${colors.flame}`} />
          {isAnimating && (
            <div className="absolute inset-0 animate-ping">
              <Flame className={`w-12 h-12 ${colors.flame} opacity-75`} />
            </div>
          )}
        </div>
        <div className="text-center">
          <div className={`text-4xl font-bold ${colors.text} transition-all duration-300 ${isAnimating ? 'scale-110' : ''}`}>
            {displayStreak}
          </div>
          <div className={`text-sm ${colors.text} font-medium mt-1`}>
            {displayStreak === 1 ? 'dia de ofensiva' : 'dias de ofensiva'}
          </div>
        </div>
      </div>

      {/* Week Calendar */}
      <WeekCalendar history={weekHistory} colorScheme={colorScheme} />

      {/* Milestone Message */}
      {daysUntilMilestone > 0 && (
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            Você está {daysUntilMilestone === 1 ? 'a um dia' : `a ${daysUntilMilestone} dias`} de uma ofensiva de {nextMilestone} dias.
          </p>
        </div>
      )}
    </div>
  );
};
